import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { 
  Scissors, 
  Calendar, 
  User, 
  Building2,
  ChevronRight,
  Star,
  CreditCard,
  Clock,
  Users,
  Gift,
  Camera,
  DollarSign,
  Send,
  Sparkles,
  Crown,
  Check,
  Play,
  TrendingUp,
  Heart,
  Award,
  Shield,
  Zap,
  Quote,
  BarChart3,
  Bell,
  CheckCircle,
  X,
  ArrowLeft,
  Share2,
  Trophy,
  Info,
  Code,
  ShoppingCart,
  Beaker,
  ArrowRight,
  Banknote,
  Loader2
} from "lucide-react";
import { API, useAuth } from "@/App";
import axios from "axios";
import AppFooter from "@/components/AppFooter";
import AppIntroduction from "@/components/AppIntroduction";
import ProfileDropdown from "@/components/ProfileDropdown";
import LookFestWinnerShowcase from "@/components/LookFestWinnerShowcase";
import { toast } from "sonner";

const PortalPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading } = useAuth();
  const [activeRole, setActiveRole] = useState(null);
  const [showMembershipTiers, setShowMembershipTiers] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null); // "stylist" or "owner"
  const [tiers, setTiers] = useState({});
  const [loadingTiers, setLoadingTiers] = useState(false);
  const [showIntro, setShowIntro] = useState(false);
  
  // Soldier code state
  const [soldierCode, setSoldierCode] = useState("");
  const [soldierValidation, setSoldierValidation] = useState(null);
  const [validatingSoldier, setValidatingSoldier] = useState(false);
  const [showCodeInput, setShowCodeInput] = useState(false);

  // Check for soldier code in URL on mount
  useEffect(() => {
    const codeFromUrl = searchParams.get("soldier") || searchParams.get("code") || "";
    if (codeFromUrl) {
      setSoldierCode(codeFromUrl.toUpperCase());
      validateSoldierCode(codeFromUrl);
    }
  }, [searchParams]);
  
  // Validate soldier code
  const validateSoldierCode = async (code) => {
    if (!code.trim()) return;
    setValidatingSoldier(true);
    try {
      const res = await axios.get(`${API}/api/membership/promo/validate/${code.trim()}`);
      if (res.data.valid) {
        setSoldierValidation(res.data);
        toast.success(`🎉 ${res.data.trial_days}-day FREE trial unlocked!`);
      } else {
        setSoldierValidation({ valid: false });
      }
    } catch (error) {
      setSoldierValidation({ valid: false });
    } finally {
      setValidatingSoldier(false);
    }
  };
  
  // Handle manual code entry
  const handleApplyCode = () => {
    if (soldierCode.trim()) {
      validateSoldierCode(soldierCode);
    }
  };
  
  // Navigate to register/lookfest with soldier code
  const handleGetStarted = () => {
    let url = '/lookfest';
    if (soldierCode && soldierValidation?.valid) {
      url += `?soldier=${soldierCode}`;
    }
    navigate(url);
  };

  // Debug auth state
  useEffect(() => {
    console.log("PortalPage auth state:", { user: user?.email, loading });
  }, [user, loading]);

  // Intro is now triggered only by user action (clicking Watch Now)
  // Removed auto-show on first visit

  const handleIntroComplete = () => {
    setShowIntro(false);
    localStorage.setItem("myhairchair_intro_seen", "true");
  };

  // Fetch membership tiers when showing the comparison
  useEffect(() => {
    if (showMembershipTiers && Object.keys(tiers).length === 0) {
      fetchTiers();
    }
  }, [showMembershipTiers]);

  const fetchTiers = async () => {
    setLoadingTiers(true);
    try {
      const res = await axios.get(`${API}/membership/tiers`);
      setTiers(res.data.tiers || {});
    } catch (error) {
      console.error("Failed to fetch tiers:", error);
    } finally {
      setLoadingTiers(false);
    }
  };

  const handleRoleClick = (roleId) => {
    if (roleId === "client") {
      navigate("/client");
    } else {
      // For stylist or owner, show membership tiers
      setSelectedRole(roleId);
      setShowMembershipTiers(true);
    }
  };

  const handleSelectTier = (tierKey) => {
    // Navigate to register with role and tier pre-selected
    const role = selectedRole === "owner" ? "owner" : "stylist";
    navigate(`/register?role=${role}&tier=${tierKey}`);
  };

  const roles = [
    {
      id: "client",
      title: "Client",
      subtitle: "Book Your Next Look",
      description: "Find and book appointments with top barbers and stylists in your area.",
      icon: User,
      color: "from-amber-400 to-yellow-500",
      shadowColor: "shadow-amber-500/30",
      borderColor: "border-amber-500/30",
      hoverBorder: "hover:border-amber-400",
      features: [
        { icon: Calendar, text: "Easy appointment booking" },
        { icon: Gift, text: "Earn loyalty rewards" },
        { icon: Star, text: "Rate & review stylists" },
        { icon: Camera, text: "Save your favorite styles" }
      ],
      link: "/client",
      cta: "Find a Stylist"
    },
    {
      id: "stylist",
      title: "Barber / Stylist",
      subtitle: "Grow Your Business",
      description: "Manage your schedule, services, and clients all in one place.",
      icon: Scissors,
      color: "from-amber-500 to-amber-600",
      shadowColor: "shadow-amber-500/30",
      borderColor: "border-amber-500/30",
      hoverBorder: "hover:border-amber-400",
      features: [
        { icon: Scissors, text: "Set your services & pricing" },
        { icon: Clock, text: "Manage your availability" },
        { icon: CreditCard, text: "Accept multiple payments" },
        { icon: Users, text: "Build your client base" }
      ],
      link: "/stylist-landing",
      cta: "Start Your Profile"
    },
    {
      id: "owner",
      title: "Salon Owner / Barber Shop",
      subtitle: "Manage Your Empire",
      description: "Run your salon or barber shop, collect booth rent, and manage your team of stylists.",
      icon: Building2,
      color: "from-yellow-500 to-amber-500",
      shadowColor: "shadow-amber-500/30",
      borderColor: "border-amber-500/30",
      hoverBorder: "hover:border-amber-400",
      features: [
        { icon: DollarSign, text: "Collect booth rent" },
        { icon: Send, text: "Send payment links" },
        { icon: Scissors, text: "All stylist features included" },
        { icon: Users, text: "Manage multiple renters" }
      ],
      link: "/owner-landing",
      cta: "Manage Your Shop"
    }
  ];

  // Hero images - Custom generated for MyHairChair (Diverse team, high-end but approachable)
  const heroImages = {
    main: "https://static.prod-images.emergentagent.com/jobs/bf1d2e2c-705d-447e-ae32-aacc715b9ac1/images/f0c1edc2c96a785917bde77259ba2f93a96e2967cee19159f82a7fa89e55fada.png", // Salon V1 - diverse team with clients
    barbers: "https://static.prod-images.emergentagent.com/jobs/bf1d2e2c-705d-447e-ae32-aacc715b9ac1/images/50540290f65f20a31cb688f78d06e1e06c5e02c0ed0085df0e322e949f369efa.png", // Barbers working side by side
    barbersPortrait: "https://static.prod-images.emergentagent.com/jobs/bf1d2e2c-705d-447e-ae32-aacc715b9ac1/images/4c1af9627370186efd894b66a574ab50c8e00c566722e49391389229831c5f73.png", // Two barbers posing
    barberCloseup: "https://static.prod-images.emergentagent.com/jobs/bf1d2e2c-705d-447e-ae32-aacc715b9ac1/images/79bfa59d60600833d052f9c9533394da668cf03b0c4d48fd1727bb08c4d67749.png", // Barber close-up with gold clippers
    fullTeam: "https://static.prod-images.emergentagent.com/jobs/bf1d2e2c-705d-447e-ae32-aacc715b9ac1/images/aa09c032ffc85dccfbfd842b12d50f2e47ac011aaacb702c33b462ca4759d16e.png", // Full diverse barbershop team
    salonFull: "https://static.prod-images.emergentagent.com/jobs/bf1d2e2c-705d-447e-ae32-aacc715b9ac1/images/c43c5488bf83fdef4a3330e9e97845b97e650a5261c54897b26be1f8a254a4c1.png" // Salon V2 - busy diverse salon
  };

  const stats = [
    { value: "$7.5B+", label: "Market Size" },
    { value: "10K+", label: "Professionals" },
    { value: "98%", label: "Satisfaction" },
    { value: "40%", label: "More Revenue" }
  ];

  // Real testimonials from stylists/barbers
  const testimonials = [
    {
      name: "Keisha Williams",
      role: "Master Braider • Atlanta, GA",
      image: "https://images.pexels.com/photos/3993449/pexels-photo-3993449.jpeg?auto=compress&w=150",
      quote: "MyHairChair changed my business. I went from chasing payments to getting paid automatically. My clients love the loyalty rewards!",
      rating: 5,
      stats: { clients: 234, years: 15 }
    },
    {
      name: "Jerome Mitchell",
      role: "Classic Barber • Houston, TX",
      image: "https://images.pexels.com/photos/7697673/pexels-photo-7697673.jpeg?auto=compress&w=150",
      quote: "20 years in the game and I wish I had this sooner. The booth rent collection alone saves me hours every month. Game changer.",
      rating: 5,
      stats: { clients: 445, years: 20 }
    },
    {
      name: "Maria Santos",
      role: "Color Specialist • Miami, FL",
      image: "https://images.pexels.com/photos/3993467/pexels-photo-3993467.jpeg?auto=compress&w=150",
      quote: "Finally, an app built for US. The booking system is smooth and my clients actually show up now because of the reminders!",
      rating: 5,
      stats: { clients: 156, years: 8 }
    }
  ];

  // Mock dashboard data for preview
  const mockDashboardData = {
    todayAppointments: [
      { time: "9:00 AM", client: "Jasmine T.", service: "Box Braids", price: 180 },
      { time: "12:00 PM", client: "Marcus W.", service: "Fade + Beard", price: 45 },
      { time: "2:30 PM", client: "Angela D.", service: "Silk Press", price: 75 },
      { time: "4:00 PM", client: "DeShawn M.", service: "Lineup", price: 25 }
    ],
    weeklyStats: {
      revenue: 2450,
      appointments: 28,
      newClients: 6,
      loyaltyRedeemed: 3
    }
  };

  return (
    <div className="min-h-screen bg-black text-white overflow-hidden">
      {/* Look Fest Contest Banner - Sticky Top */}
      <div className="bg-gradient-to-r from-amber-500 via-orange-500 to-amber-500 text-black py-2 px-4 relative z-50">
        <Link to="/lookfest" className="max-w-7xl mx-auto flex items-center justify-center gap-3 hover:opacity-90 transition-opacity">
          <Trophy className="w-5 h-5 animate-bounce" />
          <span className="font-bold text-sm sm:text-base">
            🏆 KEEP THE 100 LOOK FEST — Win $100/month + $1,000 Grand Prize!
          </span>
          <span className="hidden sm:inline text-sm font-medium bg-black/20 px-3 py-1 rounded-full">
            Enter Now →
          </span>
          <Trophy className="w-5 h-5 animate-bounce hidden sm:block" />
        </Link>
      </div>

      {/* Featured Promo Banner */}
      {/* Animated Background */}
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black"></div>
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-yellow-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 right-1/3 w-64 h-64 bg-amber-400/5 rounded-full blur-3xl"></div>
        {/* Decorative lines */}
        <div className="absolute top-20 left-0 w-full h-px bg-gradient-to-r from-transparent via-amber-500/20 to-transparent"></div>
        <div className="absolute bottom-20 left-0 w-full h-px bg-gradient-to-r from-transparent via-amber-500/20 to-transparent"></div>
      </div>

      {/* Header */}
      <header className="relative pt-3 pb-2 px-4 sm:px-6 border-b border-amber-500/20">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center shrink-0">
            <img 
              src="/images/myhairchair-logo.png" 
              alt="MyHairChair - The All-In-One Salon Management & Payment Portal" 
              className="h-10 sm:h-12 md:h-14 lg:h-16 w-auto"
            />
          </Link>
          <div className="flex items-center gap-1 sm:gap-2">
            <Button 
              variant="ghost" 
              onClick={() => setShowIntro(true)}
              className="text-amber-400 hover:text-amber-300 hover:bg-amber-500/10 hidden md:flex items-center gap-2"
            >
              <Info className="w-4 h-4" />
              How It Works
            </Button>
            <Link to="/user-guide">
              <Button variant="ghost" className="text-violet-400 hover:text-violet-300 hover:bg-violet-500/10 hidden md:flex items-center gap-2">
                <Play className="w-4 h-4" />
                Video Tutorials
              </Button>
            </Link>
            <Link to="/referral-program">
              <Button variant="ghost" className="text-amber-400 hover:text-amber-300 hover:bg-amber-500/10 hidden sm:flex items-center gap-2">
                <Trophy className="w-4 h-4" />
                Referral Program
              </Button>
            </Link>
            <Link to="/invite-stylist">
              <Button variant="ghost" className="text-purple-400 hover:text-purple-300 hover:bg-purple-500/10 hidden sm:flex items-center gap-2">
                <Share2 className="w-4 h-4" />
                Invite Stylists
              </Button>
            </Link>
            <Link to="/investors">
              <Button variant="ghost" className="text-amber-400/70 hover:text-amber-400 hover:bg-amber-500/10">
                Investors
              </Button>
            </Link>
            <Link to="/partnership">
              <Button variant="ghost" className="text-purple-400/70 hover:text-purple-400 hover:bg-purple-500/10 hidden md:flex items-center gap-1">
                <Code className="w-4 h-4" />
                Dev Partners
              </Button>
            </Link>
            {loading ? (
              <div className="w-8 h-8 rounded-full bg-amber-500/20 animate-pulse" />
            ) : user ? (
              <div className="flex items-center gap-3">
                <Button 
                  onClick={() => navigate(user.role === "stylist" || user.role === "owner" ? "/stylist" : "/dashboard")}
                  className="bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700 text-black font-semibold shadow-lg shadow-amber-500/30 hidden sm:flex"
                  data-testid="go-to-dashboard-btn"
                >
                  Dashboard
                </Button>
                <ProfileDropdown user={user} />
              </div>
            ) : (
              <Link to="/login">
                <Button variant="outline" className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10 hover:text-amber-300">
                  Sign In
                </Button>
              </Link>
            )}
          </div>
        </div>
      </header>

      {/* Hero Section - Full Width Impact */}
      <section className="relative px-6 pt-8 pb-8">
        <div className="max-w-7xl mx-auto">
          {/* Main Hero Image - Full Width */}
          <div className="relative rounded-3xl overflow-hidden mb-8 ring-2 ring-amber-500/30 shadow-2xl shadow-amber-500/20">
            {/* Main Feature Image - Diverse salon team with clients */}
            <img 
              src={heroImages.main}
              alt="Diverse team of stylists and barbers serving clients at MyHairChair salon"
              className="w-full h-[400px] sm:h-[450px] lg:h-[500px] object-cover object-center"
            />
            
            {/* Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent"></div>
            <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-transparent to-black/60"></div>
            
            {/* Content Overlay */}
            <div className="absolute inset-0 flex flex-col justify-end p-6 sm:p-10">
              <div className="max-w-3xl">
                {/* Keep the 100 Badge - Clickable */}
                <Badge 
                  className="bg-gradient-to-r from-amber-500/30 to-yellow-500/30 text-amber-300 border-amber-500/40 mb-4 px-4 py-2 text-sm font-bold backdrop-blur-sm cursor-pointer hover:from-amber-500/40 hover:to-yellow-500/40 transition-all hover:scale-105"
                  onClick={() => navigate('/membership')}
                  data-testid="keep-100-badge"
                >
                  <DollarSign className="w-4 h-4 mr-1" />
                  KEEP 100% OF YOUR EARNINGS
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Badge>
                
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-3 leading-tight">
                  <span className="bg-gradient-to-r from-amber-400 via-yellow-500 to-amber-400 bg-clip-text text-transparent">
                    Keep the 100 💯
                  </span>
                  <br />
                  <span className="text-white text-2xl sm:text-3xl lg:text-4xl">
                    Your Money. Your Clients. Zero Fees.
                  </span>
                </h2>
                
                <p className="text-lg sm:text-xl text-amber-200/90 mb-2 font-medium">
                  Accept CashApp, Zelle, or Cash — we don't take a cut.
                </p>
                <p className="text-sm text-gray-300 mb-6">
                  Want card payments? Stripe's got you covered <span className="text-gray-400">(standard fees apply)</span>
                </p>
                
                {/* Feature Pills */}
                <div className="flex flex-wrap gap-3 mb-6">
                  {[
                    { icon: Banknote, text: "CashApp/Zelle = 0% fees" },
                    { icon: CreditCard, text: "Stripe available" },
                    { icon: Check, text: "Setup in 15 min" }
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2 bg-amber-500/20 backdrop-blur-sm border border-amber-500/40 px-4 py-2 rounded-full">
                      <item.icon className="w-4 h-4 text-amber-400" />
                      <span className="text-white text-sm font-medium">{item.text}</span>
                    </div>
                  ))}
                </div>
                
                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <Link to="/find-stylists">
                    <Button className="bg-gradient-to-r from-amber-400 to-yellow-500 text-black font-bold px-8 py-6 text-lg rounded-xl shadow-lg shadow-amber-500/40 hover:shadow-amber-500/60 hover:scale-105 transition-all">
                      FIND YOUR CHAIR
                      <ChevronRight className="w-5 h-5 ml-2" />
                    </Button>
                  </Link>
                  
                  {/* Quick Google Sign In */}
                  {!user && (
                    <Button 
                      variant="outline"
                      className="bg-white/10 backdrop-blur-sm border-white/30 text-white hover:bg-white/20 px-6 py-6 text-lg rounded-xl"
                      onClick={() => {
                        localStorage.setItem("myhairchair_guide_skipped", "true");
                        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + '/dashboard')}`;
                      }}
                      data-testid="landing-google-signin"
                    >
                      <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                        <path fill="#fff" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#fff" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#fff" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#fff" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                      Quick Sign In
                    </Button>
                  )}
                </div>
                
                {/* Soldier Code Link */}
                <div className="mt-4">
                  {soldierValidation?.valid ? (
                    <div className="bg-purple-500/20 backdrop-blur-sm border border-purple-400/40 rounded-xl p-3 max-w-sm">
                      <div className="flex items-center gap-3">
                        <Shield className="w-5 h-5 text-purple-400" />
                        <div className="flex-1">
                          <p className="font-bold text-white text-sm">Referred by {soldierValidation.salon_name}</p>
                          <p className="text-xs text-purple-300">
                            <span className="font-mono">{soldierCode}</span> • {soldierValidation.trial_days}-Day FREE Trial
                          </p>
                        </div>
                        <CheckCircle className="w-4 h-4 text-green-400" />
                      </div>
                    </div>
                  ) : (
                    <>
                      {!showCodeInput ? (
                        <button
                          onClick={() => setShowCodeInput(true)}
                          className="text-purple-400 hover:text-purple-300 text-sm font-medium flex items-center gap-2"
                        >
                          <Gift className="w-4 h-4" />
                          Have a Soldier referral code?
                        </button>
                      ) : (
                        <div className="bg-white/10 backdrop-blur-sm border border-purple-400/40 rounded-xl p-3 max-w-sm">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-medium text-white flex items-center gap-1">
                              <Shield className="w-3 h-3 text-purple-400" />
                              Enter Soldier Code
                            </span>
                            <button onClick={() => setShowCodeInput(false)} className="text-gray-400 hover:text-white">
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                          <div className="flex gap-2">
                            <Input
                              placeholder="e.g. GEORGE-HQ"
                              value={soldierCode}
                              onChange={(e) => setSoldierCode(e.target.value.toUpperCase())}
                              className="flex-1 text-center font-mono uppercase bg-white/20 border-purple-400/40 text-white placeholder:text-gray-400 h-9"
                            />
                            <Button
                              onClick={handleApplyCode}
                              disabled={validatingSoldier || !soldierCode.trim()}
                              size="sm"
                              className="bg-purple-500 hover:bg-purple-600 text-white h-9"
                            >
                              {validatingSoldier ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply"}
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
              
              {/* Stats - Right Side on Desktop */}
              <div className="hidden lg:flex absolute right-10 bottom-10 gap-6">
                {stats.map((stat, idx) => (
                  <div key={idx} className="text-center bg-black/60 backdrop-blur-sm border border-amber-500/30 px-6 py-4 rounded-xl">
                    <p className="text-2xl font-bold text-amber-400">{stat.value}</p>
                    <p className="text-xs text-gray-400">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          {/* Mobile Stats */}
          <div className="flex lg:hidden flex-wrap justify-center gap-4 mb-8">
            {stats.map((stat, idx) => (
              <div key={idx} className="text-center bg-gray-900/80 border border-amber-500/30 px-5 py-3 rounded-xl">
                <p className="text-xl font-bold text-amber-400">{stat.value}</p>
                <p className="text-xs text-gray-500">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Choose Your Path Title */}
      <section className="px-6 pt-8 pb-4">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 px-4 py-2 rounded-full mb-4">
            <Sparkles className="w-4 h-4 text-amber-400" />
            <span className="text-amber-400 text-sm font-medium">Choose Your Path</span>
          </div>
          <h3 className="text-2xl sm:text-3xl font-bold text-white mb-2">
            Welcome to <span className="bg-gradient-to-r from-amber-400 to-yellow-500 bg-clip-text text-transparent">MyHairChair</span>
          </h3>
          <p className="text-gray-400 max-w-2xl mx-auto">
            MyHairChair simplifies salon business. A secure web app for automated booth rent collection, 
            easy client booking, and seamless payment processing for pros.
          </p>
        </div>
      </section>

      {/* Role Cards */}
      <section className="px-6 py-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-6">
            {roles.map((role) => {
              const IconComponent = role.icon;
              return (
                <div 
                  key={role.id}
                  className={`relative bg-gradient-to-br from-gray-900/80 to-black/80 rounded-2xl p-6 border ${role.borderColor} ${role.hoverBorder} hover:shadow-xl hover:shadow-amber-500/10 transition-all duration-300 group cursor-pointer`}
                  onMouseEnter={() => setActiveRole(role.id)}
                  onMouseLeave={() => setActiveRole(null)}
                  onClick={() => handleRoleClick(role.id)}
                >
                  {/* Icon */}
                  <div className={`w-14 h-14 bg-gradient-to-br ${role.color} rounded-xl flex items-center justify-center mb-4 shadow-lg ${role.shadowColor} group-hover:scale-110 transition-transform`}>
                    <IconComponent className="w-7 h-7 text-black" />
                  </div>

                  {/* Title */}
                  <h3 className="text-xl font-bold text-white mb-1">{role.title}</h3>
                  <p className="text-amber-400 text-sm font-medium mb-3">{role.subtitle}</p>
                  <p className="text-gray-400 text-sm mb-4">{role.description}</p>

                  {/* Features */}
                  <ul className="space-y-2 mb-6">
                    {role.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-gray-300">
                        <feature.icon className="w-4 h-4 text-amber-400/70" />
                        <span>{feature.text}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA Button */}
                  <Button 
                    className={`w-full bg-gradient-to-r ${role.color} text-black font-semibold rounded-xl shadow-lg ${role.shadowColor} hover:opacity-90`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRoleClick(role.id);
                    }}
                  >
                    {role.cta}
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features Bar */}
      <section className="px-6 py-8">
        <div className="max-w-7xl mx-auto">
          <div className="bg-gradient-to-r from-amber-500/10 via-yellow-500/10 to-amber-500/10 border border-amber-500/20 rounded-2xl p-6">
            <div className="flex flex-wrap justify-center gap-6 md:gap-8">
              {[
                { icon: Check, text: "Secure Online Booking" },
                { icon: Gift, text: "Loyalty Rewards Program" },
                { icon: Trophy, text: "Referral Program", badge: "NEW" },
                { icon: CreditCard, text: "Multiple Payment Options" },
                { icon: Camera, text: "Photo Gallery & Portfolio" },
                { icon: Shield, text: "Built for Our Community" }
              ].map((item, idx) => (
                <div key={idx} className="flex items-center gap-2 text-gray-300">
                  <div className="w-6 h-6 rounded-full bg-amber-500/20 flex items-center justify-center">
                    <item.icon className="w-3.5 h-3.5 text-amber-400" />
                  </div>
                  <span className="text-sm font-medium">{item.text}</span>
                  {item.badge && (
                    <Badge className="bg-amber-500 text-black text-xs px-1.5 py-0">{item.badge}</Badge>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* What's New Banner - Animated */}
      <section className="mx-6 my-8 rounded-2xl bg-gradient-to-r from-emerald-900/50 via-emerald-800/30 to-emerald-900/50 border border-emerald-500/30 relative overflow-hidden" data-testid="whats-new-banner">
        {/* Animated background shimmer */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-emerald-400/5 to-transparent animate-pulse"></div>
        
        <div className="relative px-6 py-8">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
            {/* Left: What's New Badge */}
            <div className="flex flex-col sm:flex-row items-center gap-4 text-center sm:text-left">
              <div className="relative flex-shrink-0">
                <div className="absolute inset-0 bg-emerald-400 rounded-full animate-ping opacity-20"></div>
                <div className="relative bg-gradient-to-r from-emerald-500 to-green-500 text-white px-4 py-2 rounded-full font-bold text-sm flex items-center gap-2 shadow-lg shadow-emerald-500/30">
                  <Sparkles className="w-4 h-4" />
                  NEW IN 2026
                </div>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-white">
                Premium Features Just Launched!
              </h3>
            </div>

            {/* Center: Feature Cards */}
            <div className="flex flex-wrap justify-center gap-3">
              {[
                { icon: ShoppingCart, label: "Smart Upsells", desc: "Boost revenue 25%" },
                { icon: Beaker, label: "Scalp Care Log", desc: "Track pH & treatments" },
                { icon: Heart, label: "Health Profiles", desc: "Allergy-safe care" }
              ].map((feature, idx) => (
                <div 
                  key={idx}
                  className="bg-black/40 backdrop-blur-sm border border-emerald-500/30 rounded-xl px-4 py-3 flex items-center gap-3 hover:border-emerald-400 hover:bg-emerald-900/20 transition-all group cursor-pointer"
                >
                  <div className="w-10 h-10 bg-gradient-to-br from-emerald-500/20 to-green-500/20 rounded-lg flex items-center justify-center group-hover:from-emerald-500 group-hover:to-green-500 transition-all">
                    <feature.icon className="w-5 h-5 text-emerald-400 group-hover:text-white transition-colors" />
                  </div>
                  <div className="hidden sm:block">
                    <div className="font-semibold text-white text-sm">{feature.label}</div>
                    <div className="text-emerald-300/70 text-xs">{feature.desc}</div>
                  </div>
                  <div className="sm:hidden">
                    <div className="font-semibold text-white text-xs">{feature.label}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Right: CTA */}
            <Link 
              to="/membership" 
              className="flex items-center gap-2 bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-400 hover:to-green-400 text-white font-semibold px-6 py-3 rounded-xl shadow-lg shadow-emerald-500/30 transition-all hover:scale-105 group whitespace-nowrap"
              data-testid="whats-new-upgrade-cta"
            >
              Upgrade to Pro
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* Demo Video Section */}
      <section className="px-6 py-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 px-4 py-2 rounded-full mb-4">
              <Play className="w-4 h-4 text-amber-400" />
              <span className="text-amber-400 text-sm font-medium">See It In Action</span>
            </div>
            <h3 className="text-2xl sm:text-3xl font-bold text-white mb-2">
              Watch How <span className="bg-gradient-to-r from-amber-400 to-yellow-500 bg-clip-text text-transparent">MyHairChair</span> Works
            </h3>
            <p className="text-gray-400 max-w-2xl mx-auto">
              See how easy it is to manage your bookings, collect payments, and grow your business.
            </p>
          </div>
          
          {/* Video Container */}
          <div className="relative max-w-4xl mx-auto">
            <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-gray-900 to-black border border-amber-500/30 shadow-2xl shadow-amber-500/10">
              {/* Video Thumbnail/Placeholder - Using barbers working image */}
              <div className="aspect-video relative">
                <img 
                  src={heroImages.barbers}
                  alt="MyHairChair Demo - Professional barbers at work"
                  className="w-full h-full object-cover object-center"
                />
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent"></div>
                
                {/* Play Button - Opens Introduction Presentation */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <button 
                    onClick={() => setShowIntro(true)}
                    className="group relative"
                    data-testid="video-play-btn"
                  >
                    <div className="absolute inset-0 bg-amber-500/30 rounded-full blur-xl animate-pulse"></div>
                    <div className="relative w-20 h-20 bg-gradient-to-br from-amber-400 to-yellow-500 rounded-full flex items-center justify-center shadow-lg shadow-amber-500/50 group-hover:scale-110 transition-transform">
                      <Play className="w-8 h-8 text-black ml-1" fill="black" />
                    </div>
                  </button>
                </div>
                
                {/* Video Info */}
                <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
                  <div>
                    <p className="text-white font-semibold">Quick Start Guide</p>
                    <p className="text-gray-400 text-sm">6 slides • Learn the platform in minutes</p>
                  </div>
                  <div className="bg-green-500/20 px-3 py-1 rounded-full">
                    <span className="text-green-400 text-sm font-medium">▶ Watch Now</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Feature highlights below video */}
            <div className="grid grid-cols-3 gap-4 mt-6">
              {[
                { icon: Calendar, title: "Book Easily", desc: "Clients book 24/7" },
                { icon: CreditCard, title: "Get Paid Fast", desc: "Multiple payment options" },
                { icon: Gift, title: "Reward Loyalty", desc: "Keep clients coming back" }
              ].map((item, idx) => (
                <div key={idx} className="text-center p-4 rounded-xl bg-gray-900/50 border border-amber-500/20">
                  <item.icon className="w-6 h-6 text-amber-400 mx-auto mb-2" />
                  <p className="text-white text-sm font-medium">{item.title}</p>
                  <p className="text-gray-500 text-xs">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Mock Dashboard Preview */}
      <section className="px-6 py-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 px-4 py-2 rounded-full mb-4">
              <BarChart3 className="w-4 h-4 text-amber-400" />
              <span className="text-amber-400 text-sm font-medium">Dashboard Preview</span>
            </div>
            <h3 className="text-2xl sm:text-3xl font-bold text-white mb-2">
              Your Business at a <span className="bg-gradient-to-r from-amber-400 to-yellow-500 bg-clip-text text-transparent">Glance</span>
            </h3>
            <p className="text-gray-400 max-w-2xl mx-auto">
              See everything you need to run your chair, all in one beautiful dashboard.
            </p>
          </div>
          
          {/* Dashboard Mock */}
          <div className="max-w-5xl mx-auto bg-gradient-to-br from-gray-900/90 to-black/90 rounded-2xl border border-amber-500/30 overflow-hidden shadow-2xl">
            {/* Dashboard Header */}
            <div className="bg-black/50 border-b border-amber-500/20 p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-yellow-500 rounded-lg flex items-center justify-center">
                  <Scissors className="w-5 h-5 text-black" />
                </div>
                <div>
                  <p className="text-white font-semibold">Good morning, Keisha! ☀️</p>
                  <p className="text-gray-500 text-xs">Wednesday, December 30, 2025</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Bell className="w-5 h-5 text-gray-400" />
                  <span className="absolute -top-1 -right-1 w-3 h-3 bg-amber-500 rounded-full text-[8px] flex items-center justify-center text-black font-bold">3</span>
                </div>
              </div>
            </div>
            
            {/* Dashboard Content */}
            <div className="p-6">
              {/* Stats Row */}
              <div className="grid grid-cols-4 gap-4 mb-6">
                {[
                  { label: "This Week", value: `$${mockDashboardData.weeklyStats.revenue.toLocaleString()}`, icon: DollarSign, color: "text-green-400" },
                  { label: "Appointments", value: mockDashboardData.weeklyStats.appointments, icon: Calendar, color: "text-blue-400" },
                  { label: "New Clients", value: mockDashboardData.weeklyStats.newClients, icon: Users, color: "text-purple-400" },
                  { label: "Rewards Used", value: mockDashboardData.weeklyStats.loyaltyRedeemed, icon: Gift, color: "text-amber-400" }
                ].map((stat, idx) => (
                  <div key={idx} className="bg-black/40 rounded-xl p-4 border border-gray-800">
                    <div className="flex items-center gap-2 mb-2">
                      <stat.icon className={`w-4 h-4 ${stat.color}`} />
                      <span className="text-gray-500 text-xs">{stat.label}</span>
                    </div>
                    <p className={`text-2xl font-bold ${stat.color}`}>{stat.value}</p>
                  </div>
                ))}
              </div>
              
              {/* Today's Appointments */}
              <div className="bg-black/40 rounded-xl p-4 border border-gray-800">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-white font-semibold">Today's Schedule</h4>
                  <span className="text-amber-400 text-sm">{mockDashboardData.todayAppointments.length} appointments</span>
                </div>
                <div className="space-y-3">
                  {mockDashboardData.todayAppointments.map((apt, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-gray-900/50 rounded-lg border border-gray-800 hover:border-amber-500/30 transition-colors">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-amber-500/20 rounded-full flex items-center justify-center">
                          <span className="text-amber-400 text-sm font-medium">{apt.client.charAt(0)}</span>
                        </div>
                        <div>
                          <p className="text-white text-sm font-medium">{apt.client}</p>
                          <p className="text-gray-500 text-xs">{apt.service}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-amber-400 font-semibold">${apt.price}</p>
                        <p className="text-gray-500 text-xs">{apt.time}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Dashboard Footer */}
            <div className="bg-gradient-to-r from-amber-500/10 to-yellow-500/10 border-t border-amber-500/20 p-4 text-center">
              <p className="text-gray-400 text-sm">This is what YOUR dashboard could look like</p>
              <Link to="/register">
                <Button className="mt-2 bg-gradient-to-r from-amber-400 to-yellow-500 text-black font-semibold hover:opacity-90">
                  Get Started Free
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="px-6 py-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 px-4 py-2 rounded-full mb-4">
              <Quote className="w-4 h-4 text-amber-400" />
              <span className="text-amber-400 text-sm font-medium">Real Stories</span>
            </div>
            <h3 className="text-2xl sm:text-3xl font-bold text-white mb-2">
              Trusted by <span className="bg-gradient-to-r from-amber-400 to-yellow-500 bg-clip-text text-transparent">Professionals</span>
            </h3>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Hear from barbers and stylists who transformed their business with MyHairChair.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, idx) => (
              <Card key={idx} className="bg-gradient-to-br from-gray-900/80 to-black/80 border-amber-500/30 hover:border-amber-400/50 transition-colors">
                <CardContent className="p-6">
                  {/* Quote */}
                  <div className="mb-4">
                    <Quote className="w-8 h-8 text-amber-500/30 mb-2" />
                    <p className="text-gray-300 text-sm leading-relaxed italic">
                      "{testimonial.quote}"
                    </p>
                  </div>
                  
                  {/* Rating */}
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 text-amber-400 fill-amber-400" />
                    ))}
                  </div>
                  
                  {/* Profile */}
                  <div className="flex items-center gap-3 pt-4 border-t border-amber-500/20">
                    <img 
                      src={testimonial.image}
                      alt={testimonial.name}
                      className="w-12 h-12 rounded-full object-cover ring-2 ring-amber-500/30"
                    />
                    <div>
                      <p className="text-white font-semibold text-sm">{testimonial.name}</p>
                      <p className="text-gray-500 text-xs">{testimonial.role}</p>
                    </div>
                  </div>
                  
                  {/* Stats */}
                  <div className="flex gap-4 mt-4 pt-4 border-t border-gray-800">
                    <div className="text-center">
                      <p className="text-amber-400 font-bold">{testimonial.stats.clients}</p>
                      <p className="text-gray-500 text-xs">Clients</p>
                    </div>
                    <div className="text-center">
                      <p className="text-amber-400 font-bold">{testimonial.stats.years}+</p>
                      <p className="text-gray-500 text-xs">Years</p>
                    </div>
                    <div className="text-center">
                      <p className="text-amber-400 font-bold">
                        <CheckCircle className="w-4 h-4 inline" />
                      </p>
                      <p className="text-gray-500 text-xs">Verified</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Sign In Link - Only show when not logged in */}
      {!user && (
        <section className="px-6 py-8 pb-16">
          <div className="max-w-7xl mx-auto text-center">
            <p className="text-gray-400 mb-4">Already have an account?</p>
            <Link to="/login">
              <Button variant="outline" className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10 hover:text-amber-300 px-8">
                Sign In
              </Button>
            </Link>
          </div>
        </section>
      )}

      {/* Look Fest Winner Showcase */}
      <LookFestWinnerShowcase />

      {/* Footer */}
      <AppFooter />

      {/* Membership Tiers Modal */}
      {showMembershipTiers && (
        <div className="fixed inset-0 bg-black/90 z-50 overflow-y-auto">
          <div className="min-h-screen px-4 py-8">
            <div className="max-w-6xl mx-auto">
              {/* Header */}
              <div className="flex items-center justify-between mb-8">
                <button 
                  onClick={() => setShowMembershipTiers(false)}
                  className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
                >
                  <ArrowLeft className="w-5 h-5" />
                  Back
                </button>
                <button 
                  onClick={() => setShowMembershipTiers(false)}
                  className="p-2 hover:bg-gray-800 rounded-full transition-colors"
                >
                  <X className="w-6 h-6 text-gray-400" />
                </button>
              </div>

              {/* Title */}
              <div className="text-center mb-10">
                <div className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-500/20 to-purple-500/20 border border-amber-500/30 px-4 py-2 rounded-full mb-4">
                  <Sparkles className="w-4 h-4 text-amber-400" />
                  <span className="text-amber-400 text-sm font-medium">
                    🎉 Introductory Pricing — Limited Time Offer!
                  </span>
                </div>
                <div className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 px-4 py-2 rounded-full mb-4 ml-2">
                  <span className="text-amber-400 text-sm font-medium">
                    {selectedRole === "owner" ? "Salon Owner / Barber Shop Plans" : "Barber / Stylist Plans"}
                  </span>
                </div>
                <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">
                  Start Your Free Trial
                </h2>
                <p className="text-gray-400 max-w-2xl mx-auto">
                  Two simple plans to fit your business. <span className="text-amber-400 font-medium">30-day FREE trial on all plans!</span>
                </p>
              </div>

              {loadingTiers ? (
                <div className="flex justify-center py-16">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
                </div>
              ) : (
                <>
                  {/* Pricing Cards */}
                  <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto mb-12">
                    {/* Pro Tier - Individual Barber/Stylist */}
                    <Card className="bg-gradient-to-br from-amber-900/30 to-amber-950/50 border-amber-500/50 overflow-hidden relative">
                      <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-amber-500 to-yellow-500 text-black text-center py-1 text-sm font-bold">
                        BARBER / STYLIST • 30-DAY FREE TRIAL
                      </div>
                      <CardContent className="p-6 pt-10">
                        <div className="mb-6">
                          <div className="flex items-center gap-2 mb-1">
                            <Crown className="w-5 h-5 text-amber-400" />
                            <h3 className="text-xl font-bold text-white">Pro</h3>
                            <Badge className="bg-green-500/20 text-green-400 text-xs">30 Days Free</Badge>
                          </div>
                          <p className="text-amber-400/80 text-sm">For individual barbers & stylists</p>
                        </div>
                        <div className="mb-1">
                          <span className="text-4xl font-bold text-white">$29.99</span>
                          <span className="text-gray-400">/month</span>
                        </div>
                        <p className="text-xs text-gray-500 mb-1">After 30-day free trial</p>
                        <p className="text-green-400 text-sm mb-4 font-medium">✨ 30-day FREE trial!</p>
                        <Button 
                          className="w-full bg-gradient-to-r from-amber-500 to-yellow-500 text-black font-bold hover:opacity-90 mb-6"
                          onClick={() => handleSelectTier("pro")}
                        >
                          Start 30-Day Free Trial
                        </Button>
                        <ul className="space-y-3">
                          <li className="flex items-center gap-2 text-sm text-gray-300">
                            <Check className="w-4 h-4 text-amber-500" />
                            <strong>Unlimited</strong> clients
                          </li>
                          <li className="flex items-center gap-2 text-sm text-gray-300">
                            <Check className="w-4 h-4 text-amber-500" />
                            CRM Dashboard
                          </li>
                          <li className="flex items-center gap-2 text-sm text-gray-300">
                            <Check className="w-4 h-4 text-amber-500" />
                            <strong>Pro Mini-Site (Custom URL)</strong>
                          </li>
                          <li className="flex items-center gap-2 text-sm text-gray-300">
                            <Check className="w-4 h-4 text-amber-500" />
                            <strong>Shareable QR Code Cards</strong>
                          </li>
                          <li className="flex items-center gap-2 text-sm text-gray-300">
                            <Check className="w-4 h-4 text-amber-500" />
                            Portfolio Gallery
                          </li>
                          <li className="flex items-center gap-2 text-sm text-gray-300">
                            <Check className="w-4 h-4 text-amber-500" />
                            PayPal, Zelle, Cash App
                          </li>
                          <li className="flex items-center gap-2 text-sm text-gray-300">
                            <Check className="w-4 h-4 text-amber-500" />
                            Loyalty rewards program
                          </li>
                          <li className="flex items-center gap-2 text-sm text-gray-300">
                            <Check className="w-4 h-4 text-amber-500" />
                            Google Calendar sync
                          </li>
                        </ul>
                      </CardContent>
                    </Card>

                    {/* Elite Tier - Salon Owner/Barber Shop */}
                    <Card className="bg-gradient-to-br from-purple-900/20 to-gray-950 border-purple-500/30 overflow-hidden relative">
                      <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-purple-500 to-purple-600 text-white text-center py-1 text-sm font-bold">
                        SALON OWNER • 30-DAY FREE TRIAL
                      </div>
                      <CardContent className="p-6 pt-10">
                        <div className="mb-6">
                          <div className="flex items-center gap-2 mb-1">
                            <Zap className="w-5 h-5 text-purple-400" />
                            <h3 className="text-xl font-bold text-white">Elite</h3>
                            <Badge className="bg-green-500/20 text-green-400 text-xs">30 Days Free</Badge>
                          </div>
                          <p className="text-purple-400/80 text-sm">For salon & barber shop owners</p>
                        </div>
                        <div className="mb-1">
                          <span className="text-4xl font-bold text-white">$49.99</span>
                          <span className="text-gray-400">/month</span>
                        </div>
                        <p className="text-xs text-gray-500 mb-1">After 30-day free trial</p>
                        <p className="text-green-400 text-sm mb-4 font-medium">✨ 30-day FREE trial!</p>
                        <Button 
                          className="w-full bg-gradient-to-r from-purple-500 to-purple-600 text-white font-bold hover:opacity-90 mb-6"
                          onClick={() => handleSelectTier("elite")}
                        >
                          Start 30-Day Free Trial
                        </Button>
                        <ul className="space-y-3">
                          <li className="flex items-center gap-2 text-sm text-gray-300">
                            <Check className="w-4 h-4 text-purple-500" />
                            Everything in Pro
                          </li>
                          <li className="flex items-center gap-2 text-sm text-gray-300">
                            <Check className="w-4 h-4 text-purple-500" />
                            <strong>Staff Management Dashboard</strong>
                          </li>
                          <li className="flex items-center gap-2 text-sm text-gray-300">
                            <Check className="w-4 h-4 text-purple-500" />
                            <strong>Commission Tracking</strong>
                          </li>
                          <li className="flex items-center gap-2 text-sm text-gray-300">
                            <Check className="w-4 h-4 text-purple-500" />
                            <strong>Payroll & W-9 Compliance</strong>
                          </li>
                          <li className="flex items-center gap-2 text-sm text-gray-300">
                            <Check className="w-4 h-4 text-purple-500" />
                            Booth/chair management
                          </li>
                          <li className="flex items-center gap-2 text-sm text-gray-300">
                            <Check className="w-4 h-4 text-purple-500" />
                            Automated rent collection
                          </li>
                          <li className="flex items-center gap-2 text-sm text-gray-300">
                            <Check className="w-4 h-4 text-purple-500" />
                            Team analytics dashboard
                          </li>
                          <li className="flex items-center gap-2 text-sm text-gray-300">
                            <Check className="w-4 h-4 text-purple-500" />
                            Custom branding colors
                          </li>
                        </ul>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Feature Comparison Table */}
                  <div className="bg-gray-900/50 rounded-2xl border border-gray-800 overflow-hidden">
                    <div className="p-6 border-b border-gray-800">
                      <h3 className="text-xl font-bold text-white">Compare All Features</h3>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-gray-800">
                            <th className="text-left p-4 text-gray-400 font-medium">Feature</th>
                            <th className="text-center p-4 text-amber-400 font-medium w-32">Pro</th>
                            <th className="text-center p-4 text-purple-400 font-medium w-32">Elite</th>
                          </tr>
                        </thead>
                        <tbody>
                          {[
                            { feature: "Booking System", pro: true, elite: true },
                            { feature: "Client Database", pro: "Unlimited", elite: "Unlimited" },
                            { feature: "CRM Dashboard", pro: true, elite: true },
                            { feature: "Appointment Reminders", pro: true, elite: true },
                            { feature: "Stripe Payments", pro: true, elite: true },
                            { feature: "PayPal, Zelle, Cash App", pro: true, elite: true },
                            { feature: "Pro Mini-Site (Custom URL)", pro: true, elite: true, isNew: true },
                            { feature: "Shareable QR Code Cards", pro: true, elite: true, isNew: true },
                            { feature: "Portfolio Gallery", pro: "5 images", elite: "5 images" },
                            { feature: "Google Calendar Sync", pro: true, elite: true },
                            { feature: "Loyalty Rewards Program", pro: true, elite: true },
                            { feature: "Staff Management", pro: false, elite: true, isNew: true },
                            { feature: "Commission Tracking", pro: false, elite: true, isNew: true },
                            { feature: "Payroll & W-9 Compliance", pro: false, elite: true, isNew: true },
                            { feature: "Booth/Chair Management", pro: false, elite: true },
                            { feature: "Automated Rent Collection", pro: false, elite: true },
                            { feature: "Team Analytics Dashboard", pro: false, elite: true },
                            { feature: "Custom Branding Colors", pro: false, elite: true },
                          ].map((row, idx) => (
                            <tr key={idx} className="border-b border-gray-800/50 hover:bg-gray-800/30">
                              <td className="p-4 text-gray-300 text-sm">
                                {row.feature}
                                {row.isNew && (
                                  <span className="ml-2 px-1.5 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded">NEW</span>
                                )}
                              </td>
                              <td className="p-4 text-center">
                                {typeof row.pro === "boolean" ? (
                                  row.pro ? (
                                    <Check className="w-5 h-5 text-amber-500 mx-auto" />
                                  ) : (
                                    <X className="w-5 h-5 text-gray-600 mx-auto" />
                                  )
                                ) : (
                                  <span className="text-amber-400 text-sm font-medium">{row.pro}</span>
                                )}
                              </td>
                              <td className="p-4 text-center">
                                {typeof row.elite === "boolean" ? (
                                  row.elite ? (
                                    <Check className="w-5 h-5 text-purple-500 mx-auto" />
                                  ) : (
                                    <X className="w-5 h-5 text-gray-600 mx-auto" />
                                  )
                                ) : (
                                  <span className="text-purple-400 text-sm font-medium">{row.elite}</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    
                    {/* Bottom CTA Row */}
                    <div className="p-4 border-t border-gray-800 bg-gray-900/30">
                      <div className="grid md:grid-cols-3 gap-4">
                        <div></div>
                        <div className="text-center">
                          <Button 
                            className="bg-gradient-to-r from-amber-500 to-yellow-500 text-black font-bold"
                            onClick={() => handleSelectTier("pro")}
                          >
                            Start Pro Trial
                          </Button>
                        </div>
                        <div className="text-center">
                          <Button 
                            className="bg-gradient-to-r from-purple-500 to-purple-600 text-white font-bold"
                            onClick={() => handleSelectTier("elite")}
                          >
                            Start Elite Trial
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* FAQ or Note */}
                  <div className="text-center mt-8 text-gray-400 text-sm">
                    <div className="inline-flex items-center gap-2 bg-green-500/10 border border-green-500/30 px-4 py-2 rounded-full mb-3">
                      <span className="text-green-400 font-medium">🎉 30-Day FREE Trial on all plans!</span>
                    </div>
                    <p>No credit card required to start your trial.</p>
                    <p className="mt-2">Questions? <Link to="/contact" className="text-amber-400 hover:underline">Contact us</Link></p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* App Introduction Modal */}
      <Dialog open={showIntro} onOpenChange={setShowIntro}>
        <DialogContent className="max-w-2xl bg-transparent border-none p-0 shadow-none" aria-describedby="intro-desc">
          <p id="intro-desc" className="sr-only">Introduction to MyHairChair platform</p>
          <AppIntroduction 
            onComplete={handleIntroComplete}
            autoPlay={true}
            showCloseButton={true}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PortalPage;
